import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import axios from 'axios';

// Types
export interface Talent {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  profilePicture?: string;
  status: 'active' | 'inactive' | 'suspended';
  verificationStatus: 'pending' | 'verified' | 'rejected';
  rating: number;
  totalReviews: number;
  totalBookings: number;
  createdAt: string;
}

interface AuthState {
  talent: Talent | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface TalentAuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  loginWithGoogle: (googleToken: string) => Promise<{ success: boolean; message: string }>;
  register: (data: any) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<{ success: boolean; message: string }>;
  resetPassword: (token: string, password: string) => Promise<{ success: boolean; message: string }>;
  updateProfile: (data: Partial<Talent>) => Promise<{ success: boolean; message: string }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; message: string }>;
  refreshAuth: () => Promise<void>;
}

// Create context
const TalentAuthContext = createContext<TalentAuthContextType | undefined>(undefined);

// Axios instance for authenticated requests
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Axios instance for public requests (no auth required)
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Auth Provider Component
interface TalentAuthProviderProps {
  children: ReactNode;
}

export const TalentAuthProvider: React.FC<TalentAuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    talent: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Setup axios interceptors
  useEffect(() => {
    // Request interceptor
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('skillseek_talent_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If error is 401 and we haven't tried to refresh token yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Only try to refresh if we have a token
          const token = localStorage.getItem('skillseek_talent_token');
          if (token) {
            try {
              // Try to verify token again
              await verifyToken();
              
              // Retry the original request
              const newToken = localStorage.getItem('skillseek_talent_token');
              if (newToken) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return api(originalRequest);
              }
            } catch (refreshError) {
              // If refresh fails, logout
              console.log('Token refresh failed, logging out');
              logout();
              return Promise.reject(refreshError);
            }
          } else {
            // No token, logout
            logout();
          }
        }

        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Dependencies removed to avoid ordering issues

  // Rate limiting for token verification using useRef
  const lastVerifyTimeRef = useRef<number>(0);
  const VERIFY_COOLDOWN = 5000; // 5 seconds cooldown

  // Logout function (moved up to be available to verifyToken)
  const logout = useCallback(() => {
    const token = localStorage.getItem('skillseek_talent_token');
    
    localStorage.removeItem('skillseek_talent_token');
    delete api.defaults.headers.common['Authorization'];
    
    setAuthState({
      talent: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
    
    // Only call logout endpoint if we have a valid token and are authenticated
    if (token && authState.isAuthenticated) {
      api.post('/talent/auth/logout').catch(error => {
        console.error('Logout logging failed:', error);
      });
    }
  }, [authState.isAuthenticated]);

  // Verify token on app load
  const verifyToken = useCallback(async () => {
    try {
      // Rate limiting: prevent too frequent verification calls
      const now = Date.now();
      if (now - lastVerifyTimeRef.current < VERIFY_COOLDOWN) {
        console.log('Token verification skipped due to rate limiting');
        return;
      }
      lastVerifyTimeRef.current = now;

      const token = localStorage.getItem('skillseek_talent_token');
      if (!token) {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      const response = await api.get('/talent/auth/verify-token');
      if (response.data.success) {
        setAuthState(prev => ({
          ...prev,
          talent: response.data.talent,
          token,
          isAuthenticated: true,
          isLoading: false,
        }));
      } else {
        console.log('Token verification failed:', response.data.message);
        logout();
      }
    } catch (error: any) {
      console.log('Token verification error:', error.response?.data?.message || error.message);
      if (error.response?.status === 401) {
        logout();
      } else if (error.response?.status === 429) {
        console.log('Rate limited - will retry later');
        // Don't logout on rate limit, just wait
        setAuthState(prev => ({ ...prev, isLoading: false }));
        // Retry after a delay
        setTimeout(() => {
          verifyToken();
        }, 10000); // Retry after 10 seconds
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    }
  }, [logout]);

  // Load token on mount
  useEffect(() => {
    const token = localStorage.getItem('skillseek_talent_token');
    if (token) {
      setAuthState(prev => ({ ...prev, token }));
      verifyToken();
    } else {
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }
  }, [verifyToken]);

  // Login function
  const login = async (email: string, password: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const response = await api.post('/talent/auth/login', { email, password });
      
      if (response.data.success) {
        const { token, talent } = response.data;
        
        // Store token
        localStorage.setItem('skillseek_talent_token', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        setAuthState({
          talent,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
        
        return { success: true, message: response.data.message };
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return { success: false, message: response.data.message };
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed. Please try again.' 
      };
    }
  };

  // Google OAuth login function
  const loginWithGoogle = async (googleToken: string) => {
    try {
      console.log('Google OAuth: Starting login process');
      console.log('Google OAuth: Token length:', googleToken.length);
      console.log('Google OAuth: Token preview:', googleToken.substring(0, 50) + '...');
      
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const response = await publicApi.post('/talent/auth/google', { 
        token: googleToken 
      });
      
      console.log('Google OAuth: Backend response:', response.data);
      
      if (response.data.success) {
        const { token, talent } = response.data;
        
        // Store token
        localStorage.setItem('skillseek_talent_token', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        setAuthState({
          talent,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
        
        return { success: true, message: response.data.message };
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return { success: false, message: response.data.message };
      }
    } catch (error: any) {
      console.error('Google OAuth: Login error:', error);
      console.error('Google OAuth: Error response:', error.response?.data);
      
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return { 
        success: false, 
        message: error.response?.data?.message || 'Google login failed. Please try again.' 
      };
    }
  };

  // Register function
  const register = async (data: any) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      // Create FormData if profile picture is included
      const formData = new FormData();
      
      // Add all form fields to FormData
      Object.keys(data).forEach(key => {
        if (key === 'profilePicture' && data[key]) {
          formData.append('profilePicture', data[key]);
        } else if (key === 'skills' && Array.isArray(data[key])) {
          formData.append('skills', JSON.stringify(data[key]));
        } else if (data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key].toString());
        }
      });

      // Use public API instance for registration (no auth required)
      const response = await publicApi.post('/talent/auth/register', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        // If registration returns a token, automatically log in the user
        if (response.data.token && response.data.user) {
          const { token, user } = response.data;
          
          // Store token
          localStorage.setItem('skillseek_talent_token', token);
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          setAuthState({
            talent: user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          setAuthState(prev => ({ ...prev, isLoading: false }));
        }
        
        return { success: true, message: response.data.message };
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return { success: false, message: response.data.message };
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      
      setAuthState(prev => ({ ...prev, isLoading: false }));
      
      // Handle specific error codes
      if (error.response?.status === 429) {
        return { 
          success: false, 
          message: 'Too many registration attempts. Please wait a few minutes before trying again.' 
        };
      } else if (error.response?.status === 409) {
        return { 
          success: false, 
          message: 'An account with this email already exists. Please use a different email or try logging in.' 
        };
      } else if (error.response?.status === 400) {
        return { 
          success: false, 
          message: error.response?.data?.message || 'Invalid registration data. Please check your information and try again.' 
        };
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        return { 
          success: false, 
          message: 'Registration service temporarily unavailable. Please try again later.' 
        };
      }
      
      return { 
        success: false, 
        message: error.response?.data?.message || 'Registration failed. Please try again later.' 
      };
    }
  };

  // Forgot password function
  const forgotPassword = async (email: string) => {
    try {
      const response = await publicApi.post('/talent/auth/forgot-password', { email });
      return { 
        success: response.data.success, 
        message: response.data.message 
      };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to send reset email.' 
      };
    }
  };

  // Reset password function
  const resetPassword = async (token: string, password: string) => {
    try {
      const response = await publicApi.post('/talent/auth/reset-password', { token, password });
      return { 
        success: response.data.success, 
        message: response.data.message 
      };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to reset password.' 
      };
    }
  };

  // Update profile function
  const updateProfile = async (data: Partial<Talent>) => {
    try {
      const response = await api.put('/talent/auth/profile', data);
      if (response.data.success) {
        setAuthState(prev => ({
          ...prev,
          talent: response.data.talent,
        }));
      }
      return { 
        success: response.data.success, 
        message: response.data.message 
      };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to update profile.' 
      };
    }
  };

  // Change password function
  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      const response = await api.put('/talent/auth/change-password', {
        currentPassword,
        newPassword,
      });
      return { 
        success: response.data.success, 
        message: response.data.message 
      };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to change password.' 
      };
    }
  };

  // Refresh auth data
  const refreshAuth = async () => {
    try {
      const response = await api.get('/talent/auth/profile');
      if (response.data.success) {
        setAuthState(prev => ({
          ...prev,
          talent: response.data.talent,
        }));
      }
    } catch (error) {
      console.error('Failed to refresh auth data:', error);
    }
  };

  // Context value
  const contextValue: TalentAuthContextType = {
    ...authState,
    login,
    loginWithGoogle,
    register,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    changePassword,
    refreshAuth,
  };

  return (
    <TalentAuthContext.Provider value={contextValue}>
      {children}
    </TalentAuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useTalentAuth = () => {
  const context = useContext(TalentAuthContext);
  if (context === undefined) {
    throw new Error('useTalentAuth must be used within a TalentAuthProvider');
  }
  return context;
};

// Export axios instance for use in other parts of the app
export { api }; 